<template>
  <main class="samsung">
    <SingleLevelBreadcrumb
      label="Samsung"
      class="samsung__breadcrumb"
    />

    <SamsungStage
      :data="stage"
      @go-to-section="goToSection"
    />

    <section class="samsung__section samsung__section--smartphones">
      <div
        ref="samsungS25Element"
        class="samsung__section-anchor"
      />
      <div
        ref="samsungS25UltraElement"
        class="samsung__section-anchor"
      />
      <div
        ref="samsungS25PlusElement"
        class="samsung__section-anchor"
      />

      <VideoHeroContent
        class="samsung__video-section"
        :video="teaserGalaxyFamily.video"
        :title="teaserGalaxyFamily.title"
        :copy="teaserGalaxyFamily.copy"
        :cta="teaserGalaxyFamily.cta"
        :hero-image-align="teaserGalaxyFamily.hero.align"
        :animation-product="teaserGalaxyFamily.hero.animationProduct"
        video-aria-label="Informationsvideo für die Samsung Galaxy Smartphones"
        play-button-aria-label="Samsung Galaxy Family Video abspielen"
        pause-button-aria-label="Samsung Galaxy Family Video pausieren"
      >
        <template #image-fallback>
          <CdnImage
            class="samsung__bg-image-fallback"
            :src="teaserGalaxyFamily.imageFallback.src"
            :alt="teaserGalaxyFamily.imageFallback.alt"
            :aria-hidden="teaserGalaxyFamily.imageFallback.ariaHidden ? 'true' : 'undefined'"
            lazy
            sizes="sm:100vw lg:80vw"
          />
        </template>
        <template #hero>
          <CdnImage
            class="samsung__hero-teaser samsung__hero-teaser--galaxy-family"
            :src="teaserGalaxyFamily.hero.src"
            :alt="teaserGalaxyFamily.hero.alt"
            lazy
            sizes="sm:100vw lg:80vw"
          />
        </template>
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <SamsungUspTeaser />
    </section>

    <section class="samsung__section">
      <div
        ref="galaxyWatch7Element"
        class="samsung__section-anchor"
      />
      <VideoHeroContent
        class="samsung__video-section"
        :row-reverse="watchesTeaser.rowReverse"
        :video="watchesTeaser.video"
        :title="watchesTeaser.title"
        :copy="watchesTeaser.copy"
        :cta="watchesTeaser.cta"
        :hero-image-align="watchesTeaser.hero?.align"
        :animation-product="watchesTeaser.hero?.animationProduct"
        video-aria-label="Präsentationsvideo der Galaxy Watch7 mit Galaxy AI"
        play-button-aria-label="Galaxy Watch7 Video abspielen"
        pause-button-aria-label="Galaxy Watch7 Video pausieren"
      >
        <template #hero>
          <CdnImage
            v-if="watchesTeaser.hero"
            class="samsung__hero-teaser samsung__hero-teaser--watches"
            :src="watchesTeaser.hero.src"
            :alt="watchesTeaser.hero.alt"
            lazy
            sizes="sm:100vw lg:80vw"
          />
        </template>
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <GridContainer>
        <ComparisonTable
          :data="comparisonTableProducts.watches"
          :heading-title="tableWatches.title"
          :sub-title="tableWatches.copy"
          class="samsung__comparison-smartwatches-table"
        >
          <template #header="{ data, sameHeight }">
            <div class="comparison-table__column">
              <ComparisonProduct
                :products="data.products"
                :products-big="data.productsBig"
                :has-same-height="sameHeight"
                aria-hidden
                class="samsung__comparison-smartwatches"
              >
                <template #image="{ itemCurrent: currentImage }">
                  <nuxt-link
                    v-if="currentImage.slug"
                    :to="currentImage.slug"
                    :title="currentImage.alt"
                    class="samsung__comparison-smartwatches-link"
                  >
                    <CdnImage
                      :alt="currentImage.alt"
                      :src="currentImage.src"
                      lazy
                      render-without-figure
                    />
                  </nuxt-link>
                  <CdnImage
                    v-else
                    :alt="currentImage.alt"
                    :src="currentImage.src"
                  />
                </template>
              </ComparisonProduct>
            </div>
          </template>

          <template #cell="{ item, feature, featureList }">
            <ComparisonItemValues
              :title="feature"
              :content="item.values[featureList.indexOf(feature)]?.content"
              :list="item.values[featureList.indexOf(feature)]?.list"
              :additional-info="item.values[featureList.indexOf(feature)]?.info"
              :is-checked="item.values[featureList.indexOf(feature)]?.checked"
            />
          </template>
        </ComparisonTable>
      </GridContainer>

      <SamsungConfigurator />
    </section>

    <section class="samsung__section">
      <div
        ref="galaxyBuds3Element"
        class="samsung__section-anchor"
      />
      <VideoHeroContent
        class="samsung__video-section"
        :row-reverse="budsTeaser.rowReverse"
        :video="budsTeaser.video"
        :title="budsTeaser.title"
        :copy="budsTeaser.copy"
        :cta="budsTeaser.cta"
        :hero-image-align="budsTeaser.hero.align"
        :animation-product="budsTeaser.hero.animationProduct"
        video-aria-label="Präsentationsvideo der Galaxy Buds3 mit Galaxy AI"
        play-button-aria-label="Galaxy Buds3 Video abspielen"
        pause-button-aria-label="Galaxy Buds3 Video pausieren"
      >
        <template #hero>
          <CdnImage
            class="samsung__hero-teaser samsung__hero-teaser--buds"
            :src="budsTeaser.hero.src"
            :alt="budsTeaser.hero.alt"
            lazy
            sizes="sm:100vw lg:80vw"
          />
        </template>
      </VideoHeroContent>
    </section>

    <section class="samsung__section">
      <GridContainer>
        <ComparisonTable
          :data="comparisonTableProducts.buds"
          :heading-title="tableBuds.title"
          :sub-title="tableBuds.copy"
        >
          <template #header="{ data, sameHeight }">
            <div class="comparison-table__column">
              <ComparisonProduct
                :products="data.products"
                :products-big="data.productsBig"
                :has-same-height="sameHeight"
                aria-hidden
                class="samsung__buds-products"
              >
                <template #image="{ itemCurrent: currentImage }">
                  <nuxt-link
                    v-if="currentImage.slug"
                    :to="currentImage.slug"
                    :title="currentImage.alt"
                    class="samsung__buds-products-link"
                  >
                    <CdnImage
                      :alt="currentImage.alt"
                      :src="currentImage.src"
                      lazy
                      render-without-figure
                    />
                  </nuxt-link>
                  <CdnImage
                    v-else
                    :alt="currentImage.alt"
                    :src="currentImage.src"
                  />
                </template>
              </ComparisonProduct>
            </div>
          </template>

          <template #cell="{ item, feature, featureList }">
            <ComparisonItemValues
              :title="feature"
              :content="item.values[featureList.indexOf(feature)]?.content"
              :list="item.values[featureList.indexOf(feature)]?.list"
              :additional-info="item.values[featureList.indexOf(feature)]?.info"
              :is-checked="item.values[featureList.indexOf(feature)]?.checked"
            />
          </template>
        </ComparisonTable>
      </GridContainer>
    </section>

    <section class="samsung__section">
      <Heading
        v-if="offerTilesSmartphones[0]?.mainTitle"
        :text="offerTilesSmartphones[0]?.mainTitle"
        class="samsung__section-title"
        font-weight="ultra"
        size="lg"
        tag="h2"
        text-uppercase
        align="center"
        spaces-top
        spaces-bottom
      />
      <ProductOfferTileSMContainer
        :items="offerTilesSmartphones"
        show-available-variant
        tile-type="medium"
        heading-tag="h2"
      />
    </section>

    <section class="samsung__section">
      <Heading
        text="Die Samsung Galaxy Family"
        class="samsung__section-title"
        font-weight="ultra"
        size="lg"
        tag="h2"
        text-uppercase
        align="center"
        spaces-top
      />
      <Heading
        align="center"
        class="samsung__section-copy"
        size="sm"
        tag="h3"
        spaces-top
        spaces-bottom
        font-weight="normal"
      >
        Die perfekte Verbindung für den ganzen Tag
      </Heading>
      <ProductOfferTileSMContainer
        :items="displayedFamilyTiles"
        show-available-variant
        tile-type="small"
        heading-tag="h4"
      />
    </section>

    <section class="samsung__section">
      <SamsungTeasers :teaser-items="teaserBanner" />
    </section>
  </main>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import {
  VideoHeroContent,
  Heading,
  GridContainer,
  ComparisonTable,
  ComparisonProduct,
  ComparisonItemValues,
} from '@i22/td-longtail-vue-components';
import {
  getOffersByGroup,
  loadOffersForPromotions,
  type PromotionOffer,
} from '~/lib/offers-by-group';

import SingleLevelBreadcrumb from '~/components/single-level-breadcrumb.vue';
import SamsungStage from '~/components/samsung/samsung-stage.vue';
import SamsungUspTeaser from '~/components/samsung/samsung-usp-teaser.vue';
import SamsungTeasers from '~/components/samsung/samsung-teasers.vue';
import CdnImage from '~/components/cdn-image.vue';
import ProductOfferTileSMContainer from '~/components/product-offer-tile-s-m-container.vue';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';
import SamsungConfigurator from '~/components/smartwatch-config/samsung-configurator.vue';
import { useLazyAsyncData } from '#imports';
import {
  TEASER_BANNER,
  STAGE_DATA,
  SMARTPHONES_GALAXY_FAMILY,
  WATCHES_GALAXY_FAMILY,
  COMPARISON_WATCHES,
  SAMSUNG_GALAXY_BUDS,
  COMPARISON_BUDS,
  PRODUCT_DATA,
} from './product-data';

Track({ page_type: 'theme.landing' });

getHeader({
  title: ref('Samsung Galaxy Family entdecken'),
  description: ref(
    'Die Geräte der Samsung Galaxy Family arbeiten perfekt zusammen. Entdecke jetzt die Vorteile und finde mit dem Galaxy Watch Konfigurator Dein Wunschmodell!'
  ),
});

const { data: offers, error: errorData } = useLazyAsyncData(
  'SamsungManufacturer',
  (): Promise<PromotionOffer[]> => loadOffersForPromotions(PRODUCT_DATA)
);

const teaserBanner = TEASER_BANNER;
const stage = STAGE_DATA;
const teaserGalaxyFamily = SMARTPHONES_GALAXY_FAMILY;
const watchesTeaser = WATCHES_GALAXY_FAMILY;
const tableWatches = COMPARISON_WATCHES;
const tableBuds = COMPARISON_BUDS;
const budsTeaser = SAMSUNG_GALAXY_BUDS;

const samsungS25Element = ref(null);
const samsungS25UltraElement = ref(null);
const samsungS25PlusElement = ref(null);
const galaxyBuds3Element = ref(null);
const galaxyWatch7Element = ref(null);

const offerTilesSmartphones = computed(() => getOffersByGroup(offers.value, 'smartphones'));

const displayedFamilyTiles = computed(() =>
  ['family_first', 'family_second', 'family_third']
    .map((group) => getOffersByGroup(offers.value, group, 1))
    .flat()
);

const comparisonTableProducts = computed(() => {
  const watchOffers = getOffersByGroup(offers.value, 'comparison_watches');
  const budsOffers = getOffersByGroup(offers.value, 'comparison_buds');

  return {
    watches: tableWatches?.items?.map((item) => ({
      ...item,
      products: item.products?.map((product) => {
        const matchingOfferSlug = watchOffers.find(
          (offer) => offer.articleNumber === product.articleNumber
        )?.offer?.product?.slug;
        return {
          ...product,
          slug: matchingOfferSlug
            ? `/geraete/${matchingOfferSlug}`
            : '/wearables/smartwatches/_samsung',
        };
      }),
      productsBig: item.productsBig?.map((product) => {
        const matchingOfferSlug = watchOffers.find(
          (offer) => offer.articleNumber === product.articleNumber
        )?.offer?.product?.slug;
        return {
          ...product,
          slug: matchingOfferSlug
            ? `/geraete/${matchingOfferSlug}`
            : '/wearables/smartwatches/_samsung',
        };
      }),
    })),
    buds: tableBuds?.items?.map((item) => ({
      ...item,
      products: item.products?.map((product) => {
        const matchingOfferSlug = budsOffers.find(
          (offer) => offer.articleNumber === product.articleNumber
        )?.offer?.product?.slug;
        return {
          ...product,
          slug: matchingOfferSlug
            ? `/geraete/${matchingOfferSlug}`
            : '/zubehoer/kopfhoerer/_samsung',
        };
      }),
    })),
  };
});

const getElement = (refString: string) => {
  if (refString === 'samsungS25') {
    return samsungS25Element.value;
  }
  if (refString === 'samsungS25Ultra') {
    return samsungS25UltraElement.value;
  }
  if (refString === 'samsungS25Plus') {
    return samsungS25PlusElement.value;
  }
  if (refString === 'galaxyBuds3') {
    return galaxyBuds3Element.value;
  }
  if (refString === 'galaxyWatch7') {
    return galaxyWatch7Element.value;
  }
  return undefined;
};

const goToSection = (refString: string) => {
  nextTick(() => {
    const element = getElement(refString);
    if (!element) return;
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
    element.focus();
  });
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.samsung {
  :deep(.video-hero-content__hero-image) {
    width: 90%;
    @include mq($mq-sm) {
      width: 70%;
    }
  }

  :deep(.video-hero-content__content .link-button--primary) {
    @include mq($mq-md) {
      padding: 0.5 * $grid-base 2 * $grid-base;
    }
  }

  &__section {
    position: relative;
    padding: $grid-base * 2 0;
    @include mq($mq-md) {
      padding: $grid-base * 4 0;
    }

    &--smartphones {
      :deep(.video-hero-content__hero-image) {
        width: 90%;
      }
    }

    &-copy {
      font-weight: normal;
    }

    &-anchor {
      position: absolute;
      left: 0;
      top: -70px;
    }
  }

  &__buds-products {
    :deep(.comparison-product__list, .comparison-product__bottom) {
      padding-top: 0;
    }

    &-link {
      display: block;
      width: 100%;
      img {
        display: block;
        width: 100%;
        aspect-ratio: 5 / 4;
      }
    }
  }
  &__video-section {
    margin-bottom: $grid-base * 4;
    @include mq($mq-md) {
      margin-bottom: $grid-base * 6;
    }

    :deep(.video-player) {
      --play-button-position-left: calc(100% - 50px);
    }
  }

  &__comparison-smartwatches {
    &-table {
      @include mq($mq-lg) {
        --data-cols: 4;
      }
    }
    &-link {
      display: block;
    }
    // comparison table has no image dimensions set:
    :deep(.comparison-product__image) img {
      width: 172px;
      height: 244px;
      display: block;
    }
  }

  &__hero-teaser {
    &--galaxy-family {
      @include mq($mq-lg) {
        text-align: right;
      }
      :deep(img) {
        aspect-ratio: 1171 / 633;
        width: 80%;
      }
    }

    &--buds {
      :deep(img) {
        aspect-ratio: 1024 / 327;
        width: 100%;
      }
    }
  }

  &__bg-image-fallback {
    :deep(img) {
      width: 100%;
      aspect-ratio: 320 / 139;
    }
  }
}
</style>
